import { useMemo, useState } from 'react';

import { FEATURED_GUESTS, RESIDENTS } from '@/data/events';
import { useI18n } from '@/providers/i18n';

import { ArtCardGrid } from './components/ArtCard';
import ArtToolbar, { type Genre, type SortKey } from './components/ArtToolbar';
import FeaturedGuestGrid from './components/FeaturedGuestGrid';

export default function ArtistsPage() {
  const { t } = useI18n();
  const [filter, setFilter] = useState<Genre>('ALL');
  const [sort, setSort] = useState<SortKey>('alpha');

  const list = useMemo(() => {
    let out = RESIDENTS.slice();
    if (filter !== 'ALL') {
      out = out.filter(
        (a) =>
          a.genres.some((g) => g.toUpperCase().includes(filter)) ||
          a.tag.toUpperCase().includes(filter),
      );
    }
    out.sort((a, b) => {
      // Guest residents always pin to the top row, regardless of user sort.
      if (a.role !== b.role) return a.role === 'GUEST RESIDENT' ? -1 : 1;
      if (sort === 'since') return Number(a.since) - Number(b.since);
      if (sort === 'tag') return a.tag.localeCompare(b.tag);
      return a.name.localeCompare(b.name);
    });
    return out;
  }, [filter, sort]);

  return (
    <section data-screen-label="artists index">
      <header className="page-h">
        <div>
          <span className="nano">/ ARTISTS / RESIDENTS / GUESTS</span>
          <h1>{t('label.artists')}</h1>
        </div>
        <span className="meta">
          {RESIDENTS.length} RESIDENTS · {FEATURED_GUESTS.length}+ GUESTS
        </span>
      </header>

      <ArtToolbar
        filter={filter}
        sort={sort}
        onFilterChange={setFilter}
        onSortChange={setSort}
      />

      <ArtCardGrid artists={list} variant="c" />

      <section className="sec">
        <header className="sec-h">
          <span className="num">/ GUESTS</span>
          <span className="title">{t('label.featured')}</span>
          <span className="meta">PAST TRANSMISSIONS</span>
        </header>
        <FeaturedGuestGrid guests={FEATURED_GUESTS} />
      </section>
    </section>
  );
}
